import React from "react";
import { useIntl } from "react-intl";
import { Link, useMatch, useResolvedPath } from "react-router-dom";

import { ReactComponent as BackSVG } from "../../assets/arrow-left-thin.svg";
import { ReactComponent as InfoSVG } from "../../assets/info.svg";
import { ReactComponent as LogoSVG } from "../../assets/logo-with-name.svg";
import { ReactComponent as LogoutSVG } from "../../assets/logout.svg";

import { useDialog } from "../../components/dialog/useDialog";
import useErrorAlert from "../../hooks/useErrorAlert";
import useOuterClickEffect from "../../hooks/useOuterClickEffect";
import CustomError from "../../service/errors/custom.error";
import { useDeviceGroupStore } from "../../store/device-group/DeviceGroupStore";
import { useLoading } from "../../store/loading/LoadingContextStore";
import { useUIState } from "../../store/ui/UIState";
import webappPlatformApi from "../../webapp-platform-api/webappPlatformApi";

import { useThemeStyles } from "../../store/ui/ThemeProvider";
import "./left-menu.scss";

function MenuLink({
  to,
  Icon,
  title,
  onClick,
}: {
  to: string;
  Icon: React.ComponentType<any>;
  title: string;
  onClick?: Function;
}) {
  const isActive = useMatch({ path: to }) != null;

  return (
    <Link
      to={to}
      className={["menu-item", isActive ? "active" : undefined]
        .filter((e) => e)
        .join(" ")}
      onClick={typeof onClick === "function" ? () => onClick() : undefined}
    >
      {Icon && (
        <span className="icon">
          <Icon />
        </span>
      )}
      <span className="title">{title}</span>
    </Link>
  );
}

function BackControl() {
  const { formatMessage } = useIntl();
  const { showConfirm } = useDialog();
  const { closeLeftMenu } = useUIState();
  const processingAsyncFn = useLoading();
  const showErrorAlert = useErrorAlert();

  const { groupStore, saveDeviceGroup } = useDeviceGroupStore();

  const userLogout = React.useCallback(async () => {
    if (
      await showConfirm({
        text: formatMessage({
          id: "login.sign_out_confirm",
          defaultMessage: "Are you sure you want to quit?",
        }),
      })
    ) {
      await webappPlatformApi.logout?.();
    }
  }, [formatMessage, showConfirm]);

  const { BackIcon, backClick } = React.useMemo(() => {
    if (webappPlatformApi.close) {
      return {
        BackIcon: BackSVG,
        backClick: webappPlatformApi.close.bind(webappPlatformApi),
      };
    }

    return { BackIcon: LogoutSVG, backClick: userLogout };
  }, [userLogout]);

  const checkAllGroups = async () => {
    const groups = groupStore.filter((g) => g.devices.length === 1);
    for (const group of groups) {
      const device = group.devices[0];

      if (
        await showConfirm({
          text: formatMessage(
            { id: "one_consumer_group" },
            {
              device_name: device.name ?? device.serialNumber,
              group_name: group.name,
            }
          ),
          cancelLabel: formatMessage({ id: "one_consumer_group.proceed" }),
          confirmLabel: formatMessage({ id: "one_consumer_group.remove" }),
        })
      ) {
        try {
          await processingAsyncFn(() =>
            saveDeviceGroup(device.id, device.type, undefined)
          );
        } catch (e) {
          console.error("Error when saving a device group", e);
          await showErrorAlert(
            e instanceof CustomError ? e.getStatusText() : undefined
          );
        }
      }
    }
  };

  return (
    <button
      type="button"
      className="control-item"
      onClick={() => {
        checkAllGroups()
          .then(() => {
            closeLeftMenu();
            return backClick();
          })
          .catch((e) => {
            // TODO: handle error
            console.error(e);
          });
      }}
    >
      <BackIcon />
    </button>
  );
}

function LeftMenu() {
  const { formatMessage } = useIntl();
  const { leftSideStyles } = useThemeStyles();
  const ref = React.useRef() as React.RefObject<HTMLDivElement>;
  const { leftMenuActive, closeLeftMenu } = useUIState();

  const mainPage = useResolvedPath("");

  useOuterClickEffect(ref, closeLeftMenu);

  return (
    <div
      className={["left-menu", leftMenuActive ? "slide-from-left" : undefined]
        .filter((e) => e)
        .join(" ")}
      style={leftSideStyles}
      ref={ref}
    >
      <div className="logo">
        <Link to={mainPage} onClick={() => closeLeftMenu()}>
          <LogoSVG
            style={{
              height: "42px",
              width: "auto",
            }}
          />
        </Link>
      </div>

      <div className="menu-list">
        <div className="control-list">
          <div className="control-item-wrap">
            <BackControl />
          </div>
          <div style={{ flex: "auto" }} />
        </div>

        <MenuLink
          to="info"
          onClick={() => closeLeftMenu()}
          Icon={InfoSVG}
          title={formatMessage({ id: "left_menu.info_screen" })}
        />
      </div>
    </div>
  );
}

export default LeftMenu;
